import { Sparkles, Lock } from "lucide-react";
import useTranslation from "../client/hooks/useTranslation";
import { cn } from "../client/utils";
import { useEdgyAccess } from "./useEdgyAccess";

/**
 * Header entry point for Edgy. Deliberately louder than the surrounding nav items —
 * this is the showcase feature — but the glow is a CSS animation on opacity only,
 * so it costs nothing on the compositor and honours prefers-reduced-motion.
 *
 * During the Max-only beta the button stays visible and clickable for every plan:
 * it shows a padlock and opens the drawer on its upgrade screen. Hiding or
 * disabling it would remove the only in-app path from "I want this" to /pricing.
 */
export default function AnalyzeMyTradingButton({
  onClick,
  className,
  compact = false,
}: {
  onClick: () => void;
  className?: string;
  compact?: boolean;
}) {
  const t = useTranslation();
  const { isLocked } = useEdgyAccess();

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={isLocked ? `${t.edgy.buttonLabel} — ${t.edgy.betaLock.lockedTooltip}` : t.edgy.buttonLabel}
      title={isLocked ? t.edgy.betaLock.lockedTooltip : undefined}
      className={cn(
        "group relative inline-flex items-center gap-2 rounded-full",
        compact ? "px-3 py-1.5 text-xs" : "px-4 py-2 text-sm",
        "font-medium text-white",
        "bg-gradient-to-r from-emerald-500 to-teal-500",
        "shadow-[0_0_0_0_rgba(16,185,129,0.5)]",
        "transition-shadow duration-300",
        "hover:shadow-[0_0_20px_2px_rgba(16,185,129,0.45)]",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 focus-visible:ring-offset-2",
        className
      )}
    >
      {/* Pulsing halo — opacity only, disabled under reduced motion. */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 rounded-full bg-emerald-400/30 opacity-0 motion-safe:animate-pulse group-hover:opacity-100"
      />
      {isLocked ? (
        <Lock className={cn("relative", compact ? "h-3.5 w-3.5" : "h-4 w-4")} />
      ) : (
        <Sparkles className={cn("relative", compact ? "h-3.5 w-3.5" : "h-4 w-4")} />
      )}
      <span className="relative whitespace-nowrap">{t.edgy.buttonLabel}</span>
    </button>
  );
}
